/*
-------------------------------
WATCH PROGRESS
The time_watched entries the Enrichment Center writes into the store as media
plays, and the Heap events that go with them. The server's own entries carry
more fields (video_claimed, video_finished_at). These don't, which is why
readers such as My Metrics also consult the credit list.
-------------------------------
*/

/** What a saved entry may hold. The server's copies carry more fields. */
export type SavedWatchEntry = {
  seconds_watched?: number | string;
  max_seconds_watched?: number | string;
  video_finished?: boolean;
};

export type WatchEntry = {
  seconds_watched: number;
  max_seconds_watched: number;
  created_at: string;
  video_finished: boolean;
  quickbase_record_id_: number | string;
};

type Heap = { track: (event: string, properties: Record<string, unknown>) => void };

/**
 * An entry at `seconds`. The furthest point never drops below the saved one,
 * and finished carries over from `saved`, unless `overrides` set them.
 */
export const buildWatchEntry = (
  recordId: number | string,
  seconds: number,
  saved?: SavedWatchEntry,
  overrides: Partial<Pick<WatchEntry, 'max_seconds_watched' | 'video_finished'>> = {},
): WatchEntry => {
  return {
    seconds_watched: seconds,
    max_seconds_watched: Math.max(Number(saved?.max_seconds_watched) || 0, seconds),
    created_at: new Date().toISOString().replace('T', ' ').replace('Z', ''),
    video_finished: !!saved?.video_finished,
    quickbase_record_id_: recordId,
    ...overrides,
  };
};

/**
 * Where a video should pick up: its saved position if it was left partway, or
 * left partway through a rewatch once finished. null means play from wherever
 * the player is.
 */
export const resumePosition = (saved?: SavedWatchEntry): number | null => {
  const seconds = Number(saved?.seconds_watched);
  if (!saved || !Number.isFinite(seconds)) return null;
  const leftPartway = saved.video_finished ? seconds !== Number(saved.max_seconds_watched) : seconds > 0;
  return leftPartway ? seconds : null;
};

/** Sends a playback event to Heap, which index.html loads everywhere but localhost. */
export const trackPlaybackEvent = (event: string, recordId: number | string, seconds: number): void => {
  (window as Window & { heap?: Heap }).heap?.track(event, { video_id: recordId, seconds_watched: seconds });
};
